/**
 * PaginationInputBuilder - Utility for building DocumentLayoutInput for pagination
 *
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 *
 * This utility constructs the complex input structure required by layoutDocumentPaginated,
 * ensuring proper formatting and structure matching Rust PageSetupIn expectations.
 *
 * ARCHITECTURE (Option A - Updated 2026-01-15):
 * - Sends document as multiple paragraphs with stable UUIDs
 * - WASM caches paragraph snapshots by ID for incremental pagination
 * - On document reload, only changed paragraphs need re-layout
 * - This approach enables paragraph-level caching for large documents
 *
 * When paragraphs parameter is provided:
 * - Uses Option A: Multi-paragraph with IDs (production)
 * When paragraphs is undefined/empty:
 * - Falls back to Option B: Single merged paragraph (compatibility)
 */

import { FixedPoint, type PageSetup } from '../../../core/types/Typesetter';
import { WasmLogger } from '../../../../../utils/loggers/WasmLogger';

/**
 * Paragraph metadata from document AST
 */
export interface ParagraphMetadata {
  id: string;
  type: string;
  textStart: number;  // Grapheme index
  textEnd: number;    // Grapheme index (exclusive)
  /** 🆕 2026-01-20: Per-paragraph line spacing (1.0, 1.15, 1.5, 2.0, etc.) - undefined = use document default */
  lineSpacing?: number;
}

/**
 * 🆕 Phase 5: Form feed (hard page break) detection result
 */
export interface FormFeedDetectionResult {
  /** Paragraph IDs that end with form feed */
  formFeedParagraphIds: string[];
  /** Total count of form feeds detected */
  formFeedCount: number;
}

/**
 * Parameters for building pagination input
 */
export interface PaginationInputParams {
  documentText: string;
  pageSetup: PageSetup;
  wasmFontUid: string;
  wasmPostscriptName: string;
  wasmFontFamily: string;
  fontWeight: number;
  fontStyle: 'normal' | 'italic' | 'oblique';
  fontSizePx: number;
  fontsWithTransferableBytes: Array<{ font_uid: string; bytes: Uint8Array }>;
  neededPages: number[];
  startPage: number;
  endPage: number;
  lookahead: number;
  /** 🆕 Option A: Paragraph metadata with stable IDs for caching */
  paragraphs?: ParagraphMetadata[];
  /** 🔥 Phase 3.4+: Line spacing multiplier (1.0 = single, 1.5 = 1.5x, 2.0 = double). Default: 1.0 */
  lineSpacingMul?: number;
}

/**
 * Build DocumentLayoutInput for pagination request
 *
 * Constructs the complete input structure required by layoutDocumentPaginated,
 * including paragraphs, page setup, pagination options, and engine versions.
 *
 * ⚠️ CRITICAL: Must match Rust typesetter_core::DocumentLayoutInput structure exactly:
 * - page_setup: PageSetup { page_size, margins_fp, columns, orientation, content_rect_fp }
 * - Field names must be snake_case to match Rust serde expectations
 *
 * @param params - Parameters for building the input structure
 * @returns DocumentLayoutInput object ready for layoutDocumentPaginated
 */
export function buildPaginationInput(params: PaginationInputParams): any {
  const {
    documentText,
    pageSetup,
    wasmFontUid,
    wasmPostscriptName,
    wasmFontFamily,
    fontWeight,
    fontStyle,
    fontSizePx,
    fontsWithTransferableBytes,
    neededPages,
    startPage,
    endPage,
    lookahead,
    paragraphs,
    lineSpacingMul = 1.0  // 🔥 Phase 3.4+: Default to single line spacing
  } = params;

  // 🆕 Option A: Multi-paragraph with IDs (when paragraphs provided)
  const useOptionA = paragraphs && paragraphs.length > 0;

  // 🔬 Phase 3.4 DIAGNOSTIC: Calculate expected line height for logging
  const fontSizeFp = FixedPoint.fromPx(fontSizePx);
  const resolvedLineSpacingMul = lineSpacingMul ?? 1.0;
  // Line height = (ascent + descent) * spacing_mul
  // ascent = font_size * 0.8, descent = font_size * 0.2
  // So base height = font_size * 1.0, scaled = font_size * spacing_mul
  const expectedLineHeightFp = Math.round(fontSizeFp * resolvedLineSpacingMul);
  const expectedLineHeightPx = fontSizePx * resolvedLineSpacingMul;

  WasmLogger.layout.lineSpacingParams({
    fontSizePx,
    fontSizeFp,
    lineSpacingMul: resolvedLineSpacingMul,
    expectedLineHeightPx,
    expectedLineHeightFp,
    paragraphId: useOptionA ? paragraphs![0]?.id : 'merged',
    paragraphIndex: 0,
    paragraphTextLength: documentText.length,
    isEmpty: documentText.length === 0,
  });

  // Build paragraph array for WASM
  const wasmParagraphs = useOptionA
    ? buildMultiParagraphInput(documentText, paragraphs, pageSetup, {
        wasmFontUid, wasmPostscriptName, wasmFontFamily, fontWeight, fontStyle, fontSizePx, fontsWithTransferableBytes, lineSpacingMul
      })
    : buildSingleParagraphInput(documentText, pageSetup, {
        wasmFontUid, wasmPostscriptName, wasmFontFamily, fontWeight, fontStyle, fontSizePx, fontsWithTransferableBytes, lineSpacingMul
      });

  // 🆕 Phase 2 Observability: Log pagination input structure
  // 🔧 FIX (2026-01-19): MS Word model uses \r for paragraph boundary, \n for soft line break
  const carriageReturnCount = (documentText.match(/\r/g) || []).length;
  const softLineBreakCount = (documentText.match(/\n/g) || []).length;
  const textSample = documentText.substring(0, 100).replace(/\r/g, '\\r').replace(/\n/g, '\\n');

  WasmLogger.layout.paginationInput({
    source: 'buildPaginationInput',
    documentTextLength: documentText.length,
    carriageReturnCount,  // 🔧 MS Word: \r = paragraph boundary
    softLineBreakCount,   // 🔧 MS Word: \n = soft line break
    detectedParagraphCount: carriageReturnCount + 1,  // Paragraphs = CR count + 1
    textSample,
    outputParagraphCount: wasmParagraphs.length,
    architecture: useOptionA ? 'OPTION_A_MULTI_PARAGRAPH' : 'OPTION_B_MERGED_PARAGRAPH',
    wasmReturnsResolvedLines: true,
    paragraphIds: useOptionA ? paragraphs!.map(p => p.id.slice(0, 8)) : ['merged'],
    neededPages,
    startPage,
    endPage,
    lookahead
  });

  return {
    paragraphs: wasmParagraphs,
    // ✅ CRITICAL FIX: Match Rust PageSetup structure exactly for layout_document_paginated_json
    // Rust PageSetup expects: page_size (snake_case), margins_fp, columns, orientation, content_rect_fp
    page_setup: {
      page_size: typeof pageSetup.page_size === 'string' ? pageSetup.page_size : 'Letter',
      margins_fp: pageSetup.margins_fp,
      columns: pageSetup.columns || 1,
      orientation: pageSetup.orientation || 'portrait',
      content_rect_fp: pageSetup.content_rect_fp
    },
    paginate_options: { line_numbers: false, columns: 1 },
    start_cursor: { para_ix: 0, line_ix: 0 },
    // 🚀 OPTIMIZATION: Only request pages we actually need (not cached)
    max_pages: Math.min(neededPages.length + 2, endPage - startPage + lookahead + 1),
    engine_versions: {
      core_version: '1.0.0',
      hb_version: '12.0.0',
      rules_version: 'Unicode-15.0',
      font_set_version: 'production-v1'
    }
  };
}

/**
 * Build single merged paragraph input (Option B - fallback)
 */
function buildSingleParagraphInput(
  documentText: string,
  pageSetup: PageSetup,
  fontParams: {
    wasmFontUid: string;
    wasmPostscriptName: string;
    wasmFontFamily: string;
    fontWeight: number;
    fontStyle: 'normal' | 'italic' | 'oblique';
    fontSizePx: number;
    fontsWithTransferableBytes: Array<{ font_uid: string; bytes: Uint8Array }>;
    lineSpacingMul?: number;  // 🔥 Phase 3.4+
  }
): any[] {
  return [{
    text_runs: [{
      text: documentText,
      font: {
        fingerprint: {
          font_uid: fontParams.wasmFontUid,
          postscript_name: fontParams.wasmPostscriptName,
          family: fontParams.wasmFontFamily,
          weight: fontParams.fontWeight,
          style: fontParams.fontStyle,
        },
        size_fp: FixedPoint.fromPx(fontParams.fontSizePx),
        axes: null,
        features: null,
      },
      script: 'Latn',
      lang: 'en',
      dir: 'LTR',
      features: null,
    }],
    line_width_fp: pageSetup.content_rect_fp.w_fp,
    origin_x_fp: pageSetup.content_rect_fp.x_fp,
    origin_y_fp: pageSetup.content_rect_fp.y_fp,
    alignment: 'left',
    word_spacing_scale_fp: (pageSetup as any).word_spacing_scale_fp ?? 64,
    // 🔥 Phase 3.4+: Pass default font size (fixed-point) for empty line height calculation
    default_font_size_fp: FixedPoint.fromPx(fontParams.fontSizePx),
    // 🔥 Phase 3.4+: Pass line spacing multiplier (1.0 = single, 1.5, 2.0, etc.)
    line_spacing_mul: fontParams.lineSpacingMul ?? 1.0,
    engine_versions: {
      core_version: '1.0.0',
      hb_version: '12.0.0',
      rules_version: 'Unicode-15.0',
      font_set_version: 'production-v1'
    },
    fonts: fontParams.fontsWithTransferableBytes
  }];
}

/**
 * Build multi-paragraph input with IDs (Option A - production)
 *
 * Splits document text by paragraph boundaries and assigns stable IDs.
 * WASM can cache paragraph snapshots by ID for incremental re-layout.
 */
function buildMultiParagraphInput(
  documentText: string,
  paragraphs: ParagraphMetadata[],
  pageSetup: PageSetup,
  fontParams: {
    wasmFontUid: string;
    wasmPostscriptName: string;
    wasmFontFamily: string;
    fontWeight: number;
    fontStyle: 'normal' | 'italic' | 'oblique';
    fontSizePx: number;
    fontsWithTransferableBytes: Array<{ font_uid: string; bytes: Uint8Array }>;
    lineSpacingMul?: number;  // 🔥 Phase 3.4+
  }
): any[] {
  // Use Intl.Segmenter for accurate grapheme-to-character conversion
  const segmenter = new (Intl as any).Segmenter('en', { granularity: 'grapheme' });
  const segments = Array.from(segmenter.segment(documentText)) as Array<{ segment: string; index: number }>;

  // ✅ FIX (2026-01-16): Keep empty paragraphs (MS Word model)
  // Empty paragraphs (consecutive \r) are valid and should be sent to WASM
  // They represent intentional vertical spacing from user pressing ENTER multiple times
  // Each empty paragraph gets its own line with zero width but valid Y position
  // 🔧 (2026-01-19): MS Word uses \r for paragraph boundary, not \n\n
  return paragraphs.map((para, idx) => {
    // Convert grapheme indices to character indices for text slicing
    const charStart = para.textStart < segments.length ? segments[para.textStart].index : documentText.length;
    const charEnd = para.textEnd < segments.length
      ? segments[para.textEnd].index
      : documentText.length;

    // Extract paragraph text
    const paraText = documentText.slice(charStart, charEnd);

    // 🆕 Phase 5: Detect form feed (hard page break) at end of paragraph
    // Form feed is \f (0x0C), may appear as \f or \f\n at paragraph end
    const hasFormFeed = paraText.includes('\f');

    // 🔥 FIX 2026-01-20: Per-paragraph line spacing with fallback chain:
    // 1. para.lineSpacing (per-paragraph from AST) - highest priority
    // 2. fontParams.lineSpacingMul (document-level default)
    // 3. 1.0 (ultimate fallback = single spacing)
    const resolvedLineSpacing = para.lineSpacing !== undefined
      ? para.lineSpacing                      // Per-paragraph override from AST
      : (fontParams.lineSpacingMul ?? 1.0);   // Document default or single spacing

    // 🔬 DIAGNOSTIC: Log per-paragraph line spacing (especially when per-paragraph override is active)
    const isEmptyParagraph = paraText.length === 0 || paraText === '\r';
    const hasPerParagraphOverride = para.lineSpacing !== undefined;
    if (hasPerParagraphOverride || isEmptyParagraph || idx < 5) {
      const fontSizeFp = FixedPoint.fromPx(fontParams.fontSizePx);
      const expectedLineHeightFp = Math.round(fontSizeFp * resolvedLineSpacing);
      const expectedLineHeightPx = fontParams.fontSizePx * resolvedLineSpacing;
      WasmLogger.layout.lineSpacingParams({
        fontSizePx: fontParams.fontSizePx,
        fontSizeFp,
        lineSpacingMul: resolvedLineSpacing,
        perParagraphOverride: para.lineSpacing,  // 🆕 Show the per-paragraph value if present
        documentDefault: fontParams.lineSpacingMul,  // 🆕 Show document default for comparison
        expectedLineHeightPx,
        expectedLineHeightFp,
        paragraphId: para.id,
        paragraphIndex: idx,
        paragraphTextLength: paraText.length,
        isEmpty: isEmptyParagraph,
      });
    }

    return {
      // 🆕 Option A: Include paragraph ID for WASM caching
      paragraph_id: para.id,
      // 🆕 Phase 5: Flag for hard page break after this paragraph
      has_form_feed: hasFormFeed,
      text_runs: [{
        text: paraText,
        font: {
          fingerprint: {
            font_uid: fontParams.wasmFontUid,
            postscript_name: fontParams.wasmPostscriptName,
            family: fontParams.wasmFontFamily,
            weight: fontParams.fontWeight,
            style: fontParams.fontStyle,
          },
          size_fp: FixedPoint.fromPx(fontParams.fontSizePx),
          axes: null,
          features: null,
        },
        script: 'Latn',
        lang: 'en',
        dir: 'LTR',
        features: null,
      }],
      line_width_fp: pageSetup.content_rect_fp.w_fp,
      origin_x_fp: pageSetup.content_rect_fp.x_fp,
      origin_y_fp: pageSetup.content_rect_fp.y_fp,
      alignment: 'left',
      word_spacing_scale_fp: (pageSetup as any).word_spacing_scale_fp ?? 64,
      // 🔥 Phase 3.4+: Pass default font size (fixed-point) for empty line height calculation
      default_font_size_fp: FixedPoint.fromPx(fontParams.fontSizePx),
      // 🔥 FIX 2026-01-20: Use resolved line spacing (per-paragraph or document default)
      line_spacing_mul: resolvedLineSpacing,
      engine_versions: {
        core_version: '1.0.0',
        hb_version: '12.0.0',
        rules_version: 'Unicode-15.0',
        font_set_version: 'production-v1'
      },
      // Only attach fonts on first paragraph to avoid duplicating large byte arrays
      fonts: idx === 0 ? fontParams.fontsWithTransferableBytes : []
    };
  });
}

/**
 * 🆕 Phase 5: Detect form feeds in document text and return affected paragraph IDs
 *
 * Form feed (0x0C, \f) indicates a hard page break (Ctrl+Enter).
 * This function scans paragraphs and identifies which ones contain form feeds.
 *
 * @param documentText - Full document text
 * @param paragraphs - Paragraph metadata with grapheme indices
 * @returns FormFeedDetectionResult with paragraph IDs containing form feeds
 */
export function detectFormFeeds(
  documentText: string,
  paragraphs: ParagraphMetadata[]
): FormFeedDetectionResult {
  if (!paragraphs || paragraphs.length === 0) {
    return { formFeedParagraphIds: [], formFeedCount: 0 };
  }

  // Use Intl.Segmenter for accurate grapheme-to-character conversion
  const segmenter = new (Intl as any).Segmenter('en', { granularity: 'grapheme' });
  const segments = Array.from(segmenter.segment(documentText)) as Array<{ segment: string; index: number }>;

  const formFeedParagraphIds: string[] = [];

  for (const para of paragraphs) {
    // Convert grapheme indices to character indices
    const charStart = para.textStart < segments.length ? segments[para.textStart].index : documentText.length;
    const charEnd = para.textEnd < segments.length ? segments[para.textEnd].index : documentText.length;

    // Extract and check for form feed
    const paraText = documentText.slice(charStart, charEnd);
    if (paraText.includes('\f')) {
      formFeedParagraphIds.push(para.id);
    }
  }

  if (formFeedParagraphIds.length > 0) {
    WasmLogger.layout.paginationInput({
      architecture: 'FORM_FEED_DETECTED',
      phase: 'phase_5',
      formFeedCount: formFeedParagraphIds.length,
      formFeedParagraphIds: formFeedParagraphIds.map(id => id.slice(0, 8)),
    });
  }

  return {
    formFeedParagraphIds,
    formFeedCount: formFeedParagraphIds.length,
  };
}

